/**
 * Device identity and per-plate authorization. Replaces the legacy
 * `device-auth.js` with a clean API. The watcher lifecycle lives in
 * `deviceWatcher.js`.
 *
 * Device ID is generated once per install and persisted in localStorage. It
 * survives logout (so re-login from the same device is recognised) but is
 * regenerated if cleared.
 */

import { auth, ref } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { Auth } from '../../app/config.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('device');

/**
 * Stable device identifier. Generated on first call and persisted.
 * @returns {string}
 */
export function getDeviceId() {
  let id = LocalState.deviceId;
  if (id && id.length >= 8) return id;
  id = generateId();
  LocalState.deviceId = id;
  return id;
}

function generateId() {
  // 16-char random base36; sufficient for device-uniqueness within the user pool
  const a = Math.random().toString(36).slice(2, 10);
  const b = Math.random().toString(36).slice(2, 10);
  return (a + b).padEnd(16, '0');
}

/**
 * Authorize this device for a given plate. Writes
 *   vehicles/{plate}/devices/{deviceId}: { lastSeen }
 *
 * @param {string} plate
 * @returns {Promise<void>}
 */
export async function authorizeForPlate(plate) {
  const p = normalizePlate(plate);
  if (!p) return;
  const deviceId = getDeviceId();
  await ref(`vehicles/${p}/devices/${deviceId}`).update({
    lastSeen: Date.now(),
  });
}

/**
 * Touch this device's lastSeen — used periodically while logged in to mark
 * the device as alive. Lighter-weight than authorizeForPlate (no _bypass).
 * @param {string} plate
 */
export async function touchForPlate(plate) {
  const p = normalizePlate(plate);
  if (!p) return;
  const deviceId = getDeviceId();
  await ref(`vehicles/${p}/devices/${deviceId}/lastSeen`).set(Date.now());
}

/**
 * Remove this device's authorization (called when logging out from this device).
 * @param {string} plate
 */
export async function revokeForPlate(plate) {
  const p = normalizePlate(plate);
  if (!p) return;
  const deviceId = getDeviceId();
  try {
    await ref(`vehicles/${p}/devices/${deviceId}`).remove();
  } catch (err) {
    log.warn('revoke failed:', /** @type {Error} */ (err).message);
  }
}

/**
 * Check whether this device is trusted for the given **phone** — the strong
 * trust marker written by the server after a successful OTP verification.
 *
 * Unlike `isDeviceTrustedForPlate`, this lookup is anchored to the phone
 * (the OTP identity), not to the currently-owned vehicle. So:
 *   - Replacing a vehicle does NOT void the trust.
 *   - Admin actions on `vehicles/{plate}/devices` do NOT void the trust.
 *   - Transferring ownership of a plate to another user does NOT void it.
 *
 * The entry is written by the `verifyOtp` Cloud Function (server-side, admin
 * SDK) so the client can't fake a trusted-device claim.
 *
 * @param {string} phoneDigits  normalized phone (digits, leading 0)
 * @returns {Promise<boolean>}
 */
export async function isDeviceTrustedForPhone(phoneDigits) {
  if (!phoneDigits) return false;
  try {
    const deviceId = getDeviceId();
    const snap = await ref(`trustedDevices/${phoneDigits}/${deviceId}`).once('value');
    return snap.exists();
  } catch (err) {
    log.warn('isDeviceTrustedForPhone read failed:', /** @type {Error} */ (err).message);
    return false;
  }
}

/**
 * Promote this device to `lastActiveDevice` for the current user. Watchers
 * elsewhere react to changes on this field to log out stale devices.
 * @param {string} uid
 * @returns {Promise<void>}
 */
export async function setAsLastActive(uid) {
  if (!uid) return;
  await ref(`users/${uid}/lastActiveDevice`).set({
    deviceId: getDeviceId(),
    at: Date.now(),
  });
}

/**
 * Check whether this device is the user's current `lastActiveDevice`.
 * @param {string} uid
 * @returns {Promise<boolean>}
 */
export async function isLastActive(uid) {
  if (!uid) return false;
  const snap = await ref(`users/${uid}/lastActiveDevice/deviceId`).once('value');
  return snap.val() === getDeviceId();
}

/**
 * Soft-disconnect this device when the watcher detects a revocation:
 * signs out, clears session state (KEEPS deviceId/phone/termsAccepted so
 * the next sign-in skips OTP), and surfaces the login screen — WITHOUT
 * a page reload.
 *
 * The legacy `hardLogout` used to call `location.reload()`, producing the
 * "the app restarted out of nowhere" experience users complained about.
 * The function name stays for compatibility with `deviceWatcher.js`; the
 * behavior is now intentionally soft.
 *
 * When `userNotice` is provided, a modal explaining the disconnect is
 * shown BEFORE the user is sent to the login screen — so they don't
 * experience an inexplicable "I was kicked out of nowhere" moment.
 *
 * @param {string} [reason]
 * @param {{ message: string; button?: string; badge?: string; iconSvg?: string } | null} [userNotice]
 */
export async function hardLogout(reason = 'manual', userNotice = null) {
  log.warn('disconnect (soft):', reason);
  try { await auth().signOut(); } catch { /* ignore */ }
  // `clearAll` preserves deviceId / phone / termsAccepted by design — see
  // `localState.js::KEEP_KEYS`. Do NOT call `nukeEverything` here.
  LocalState.clearAll();
  try { sessionStorage.clear(); } catch { /* ignore */ }
  // Optional user-facing modal so the disconnect doesn't feel arbitrary.
  // Uses `showRichAlert` (icon + badge + warm gradient button) — the
  // disconnect moment benefits from a softer, reassuring treatment over
  // the plain `showAlert`.
  if (userNotice) {
    try {
      const alertMod = await import('../../core/alert.js');
      await alertMod.showRichAlert({
        message: userNotice.message,
        button: userNotice.button || 'הבנתי',
        badge: userNotice.badge,
        iconSvg: userNotice.iconSvg,
      });
    } catch (err) {
      log.warn('disconnect notice failed to render:', err);
    }
  }
  // Show the login screen without a page reload. The legacy global is set
  // up by screen-nav.js / bootstrap.
  const w = /** @type {any} */ (window);
  try { w.showLoginScreen?.(); } catch { /* ignore */ }
}
